import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import PostCard from '../components/PostCard';
import AuthModal from '../components/AuthModal';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, TrendingUp, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [post, setPost] = useState(null);
  const [skippedIds, setSkippedIds] = useState([]);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'super_admin') navigate('/super-admin');
      else navigate('/home');
    } else {
      loadPost();
    }
  }, [user, navigate]);

  const loadPost = async () => {
    try {
      const res = await axios.get(`${API}/posts/feed/public?skip_ids=${skippedIds.join(',')}`);
      if (res.data.post) {
        setPost(res.data.post);
      }
    } catch (err) {
      console.error('Error loading post:', err);
    }
  };

  const handleSkip = () => {
    if (post) {
      setSkippedIds([...skippedIds, post.id]);
      loadPost();
    }
  };

  const handleAuthRequired = () => {
    setShowAuth(true);
    setAuthMode('login');
  };

  if (user) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Share Your Thoughts.
              </span>
              <br />
              <span className="text-gray-900 dark:text-white">Anonymously.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
              Share your thoughts. Vote on posts. Stay anonymous. Join the conversation without revealing your identity.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                data-testid="get-started-btn"
                onClick={() => {
                  setShowAuth(true);
                  setAuthMode('signup');
                }}
                size="lg"
                className="btn-primary px-8 shadow-lg hover:shadow-xl transition-all"
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="login-btn"
                onClick={() => {
                  setShowAuth(true);
                  setAuthMode('login');
                }}
                variant="outline"
                size="lg"
                className="px-8 border-2 border-gray-300 hover:border-cyan-500 hover:bg-cyan-50 transition-all"
              >
                Login
              </Button>
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6 rounded-2xl card-premium">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-100 text-cyan-600 mb-4">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Anonymous</h3>
              <p className="text-gray-600">Stay anonymous with auto-generated usernames and avatars</p>
            </div>
            <div className="text-center p-6 rounded-2xl card-premium">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 text-blue-600 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Vote & Comment</h3>
              <p className="text-gray-600">Agree, disagree, or stay neutral on any topic</p>
            </div>
            <div className="text-center p-6 rounded-2xl card-premium">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 mb-4">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Trending</h3>
              <p className="text-gray-600">See what's trending and most controversial</p>
            </div>
          </div>

          {/* Sample Post */}
          {post && (
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-semibold text-center mb-6">Preview a Post</h2>
              <PostCard
                post={post}
                onSkip={handleSkip}
                onAuthRequired={handleAuthRequired}
                isPublic={true}
              />
            </div>
          )}
        </div>
      </div>

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </div>
  );
};

export default Landing;